export interface DeferredRead<T = void> {
    readonly promise: Promise<T>;
    readonly isSettled: () => boolean;
    readonly isSucceed: () => boolean;
    readonly getValue: () => T | undefined;
    /**
     * Like a `then`, but synchronous if the promise already resolved
     * Does not use the `placeholderValue` that is passed to the constructor.
     */
    readonly nowOrThen: (then: (result: T) => void) => void;
}

export interface DeferredControl<T = void, K = unknown> {
    readonly resolve: (result: T) => void,
    readonly reject: (error: K) => void
}

export class Deferred<T = void, K = unknown> {

    // Static

    public static resolve<T2>(value: T2) {
        const result = new Deferred(value);
        result.resolve(value);
        return result;
    }

    // public

    public readonly read:    DeferredRead<T>;
    public readonly control: DeferredControl<T>;

    // private

    private readonly resolveFunc: (result: T) => void;
    private readonly rejectFunc:  (error: K)  => void;

    private readonly promise: Promise<T>;

    // Dynamic

    private settled: boolean = false;
    private succeed: boolean = false;

    private value: T | undefined = undefined;

    //

    /**
     * @param placeholderValue Value to return from `.read.getValue()` until the promise is resolved
     */
    constructor(placeholderValue?: T) {

        // Deconstruct a native promise

        let resolveFunc: undefined | ((result: T) => void);
        let rejectFunc:  undefined | ((error: K) => void);
        this.promise = new Promise(
            (
                resolve: (result: T) => void,
                reject:  (error: K) => void,
            ): void => {
                resolveFunc = resolve;
                rejectFunc = reject;
            },
        );
        this.resolveFunc = resolveFunc!;
        this.rejectFunc  = rejectFunc!;

        //

        this.value = placeholderValue;

        this.read = {
            promise: this.promise,
            isSettled: this.isSettled,
            isSucceed: this.isSucceed,
            getValue: this.getValue,
            nowOrThen: this.nowOrThen,
        };

        this.control = {
            resolve: this.resolve,
            reject: this.reject,
        };
    }

    // Control part

    private resolve = (result: T): void => {
        if (this.settled) return;
        this.settled = true;
        this.succeed = true;
        this.value = result;
        this.resolveFunc(this.value);
    }

    private reject = (error: K): void => {
        if (this.settled) return;
        this.settled = true;
        this.rejectFunc(error);
    }

    // Read part

    private isSettled = (): boolean => this.settled;
    private isSucceed = (): boolean => this.succeed;
    private getValue = (): T | undefined => this.value;

    private nowOrThen = (onSuccess: (result: T) => void) => {
        if (this.isSucceed()) {
            onSuccess(this.getValue()!);
        } else {
            this.promise.then(onSuccess);
        }
    };
}
